/**
 * WorkspaceProfilePage.jsx
 * Route: /workspace/profile?workspace_id=...
 *
 * Sections: Business · Contact · Product · Market · Traction ·
 *           Brand · Operations · Goals · Constraints · Assets · Files
 *
 * NOTE: No useBlocker — uses manual confirm modal + beforeunload only.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { apiFetch } from '../services/api';
import { auth } from '../services/firebase';
import {
  AppShell, TopNav, NavDrawer, BodyGrid, PageBody, useDrawer,
} from '../components/Layout';
import '../styles/forms.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3070';

async function getToken() {
  try { return await auth.currentUser?.getIdToken(); } catch { return null; }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FILE_CATEGORIES = [
  { value: 'logo',            label: 'Logo'            },
  { value: 'profile_picture', label: 'Profile Picture' },
  { value: 'product_picture', label: 'Product Picture' },
  { value: 'document',        label: 'Document'        },
  { value: 'spreadsheet',     label: 'Spreadsheet'     },
  { value: 'presentation',    label: 'Presentation'    },
  { value: 'video',           label: 'Video'           },
  { value: 'other',           label: 'Other'           },
];

const STAGES = ['Idea','Pre-seed','Seed','Series A','Series B+','Bootstrapped','Revenue Stage'];

function fileIcon(t = '') {
  if (t.startsWith('image/'))                                       return '🖼️';
  if (t.includes('pdf'))                                            return '📄';
  if (t.includes('spreadsheet') || t.includes('csv'))              return '📊';
  if (t.includes('presentation'))                                   return '📑';
  if (t.includes('video'))                                          return '🎬';
  if (t.includes('word') || t.includes('document'))                return '📝';
  return '📎';
}

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024)        return `${b} B`;
  if (b < 1048576)     return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

// ─── Empty profile ────────────────────────────────────────────────────────────

const EMPTY = {
  business:    { name:'', tagline:'', stage:'', founded:'' },
  contact:     { website:'', address:'', phone:[], email:'',
                 socials:{ instagram:'', x:'', tiktok:'', facebook:'' } },
  product:     { core_offering:'', pricing_model:'', key_features:[] },
  market:      { icp:{ persona:'', industry:'', pain_points:[], buying_motivation:[] },
                 tam_estimate:'', competitors:[] },
  traction:    { users:0, revenue:0, key_wins:[] },
  brand:       { tone:'', colors:[] },
  operations:  { suppliers:[], key_processes:[] },
  goals:       { short_term:[], long_term:[] },
  constraints: { budget:'', time:'', team_size:'', notes:'' },
  assets:      { landing_pages:[], content:[], campaigns:[] },
};

function clone(o) { return JSON.parse(JSON.stringify(o)); }

function mergeProfile(saved) {
  function deep(target, source) {
    const out = { ...target };
    for (const k of Object.keys(source || {})) {
      if (source[k] !== null && typeof source[k] === 'object' && !Array.isArray(source[k])) {
        out[k] = deep(target[k] || {}, source[k]);
      } else {
        out[k] = source[k] ?? target[k];
      }
    }
    return out;
  }
  return deep(EMPTY, saved || {});
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared primitives
// ═══════════════════════════════════════════════════════════════════════════════

function SectionCard({ title, desc, children }) {
  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div className="pf-card-header">
        <h2 className="pf-card-title">{title}</h2>
        {desc && <p className="pf-card-desc">{desc}</p>}
      </div>
      <div className="pf-section" style={{ borderBottom: 'none' }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children, half }) {
  return (
    <div className="pf-field" style={half ? { flex: '1 1 180px' } : {}}>
      <label className="pf-label">{label}</label>
      {children}
      {hint && <p className="pf-hint">{hint}</p>}
    </div>
  );
}

function TInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input className="pf-input" type={type} value={value ?? ''}
      onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function TArea({ value, onChange, placeholder }) {
  return (
    <textarea className="pf-input pf-textarea" value={value ?? ''}
      onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function ArrayField({ label, hint, values = [], onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setDraft('');
  }
  return (
    <div className="pf-field">
      <label className="pf-label">{label}</label>
      {values.length > 0 && (
        <div className="pf-tag-list" style={{ marginBottom: 6 }}>
          {values.map((v, i) => (
            <span key={i} className="pf-tag">
              {v}
              <button className="pf-tag-remove" type="button"
                onClick={() => onChange(values.filter((_, j) => j !== i))}>✕</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="pf-input" style={{ flex: 1 }} value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder || `Add ${label.toLowerCase()}…`} />
        <button type="button" onClick={add} style={{
          padding: '9px 14px', background: 'var(--paper-3)', border: '1px solid var(--paper-3)',
          borderRadius: 8, fontSize: 12, fontFamily: 'var(--sans)', cursor: 'pointer',
          whiteSpace: 'nowrap', color: 'var(--ink)',
        }}>+ Add</button>
      </div>
      {hint && <p className="pf-hint">{hint}</p>}
    </div>
  );
}

function ActionBar({ dirty, saving, savedMsg, onSave, onCancel }) {
  return (
    <div className="pf-action-bar">
      <button className="pf-btn-save" onClick={onSave} disabled={saving || !dirty}>
        {saving ? <><span className="pf-spinner" />Saving…</> : '💾 Save Changes'}
      </button>
      <button className="pf-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
      {savedMsg && <span className="pf-success-msg">✓ Saved</span>}
    </div>
  );
}

function LeaveModal({ onStay, onDiscard }) {
  return (
    <div className="pf-modal-backdrop">
      <div className="pf-modal" style={{ maxWidth: 380 }}>
        <div className="pf-modal-header">
          <h3 className="pf-modal-title">Unsaved Changes</h3>
          <button className="pf-modal-close" onClick={onStay}>✕</button>
        </div>
        <div className="pf-modal-body">
          <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: 0, lineHeight: 1.6 }}>
            You have unsaved changes. If you leave now, your edits will be lost.
          </p>
        </div>
        <div className="pf-modal-footer">
          <button className="pf-btn-cancel" onClick={onStay}>Keep Editing</button>
          <button className="pf-btn-danger" onClick={onDiscard}>Discard & Leave</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Files section
// ═══════════════════════════════════════════════════════════════════════════════

function UploadModal({ workspaceId, onClose, onUploaded }) {
  const [title, setTitle]         = useState('');
  const [category, setCategory]   = useState('other');
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [dragging, setDragging]   = useState(false);
  const inputRef                  = useRef();

  function pickFile(f) {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError('File exceeds 5 MB limit.'); return; }
    setFile(f);
    setError('');
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  async function handleUpload() {
    if (!file || !title.trim()) { setError('Title and file are required.'); return; }
    setUploading(true);
    setError('');
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('category', category);
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/files`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      onUploaded(await res.json());
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="pf-modal-backdrop">
      <div className="pf-modal">
        <div className="pf-modal-header">
          <h3 className="pf-modal-title">Upload File</h3>
          <button className="pf-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="pf-modal-body">
          <div
            className={`pf-dropzone${dragging ? ' pf-dropzone--active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files[0]); }}
          >
            <div className="pf-dropzone-icon">{file ? fileIcon(file.type) : '📁'}</div>
            <p className="pf-dropzone-text">{file ? file.name : 'Click or drag a file here'}</p>
            <p className="pf-dropzone-hint">{file ? fmtSize(file.size) : 'Max 5 MB'}</p>
            <input ref={inputRef} type="file" style={{ display: 'none' }}
              onChange={e => pickFile(e.target.files[0])} />
          </div>

          <div className="pf-field">
            <label className="pf-label">Title <span style={{ color: 'var(--accent)' }}>*</span></label>
            <input className="pf-input" value={title}
              onChange={e => setTitle(e.target.value)} placeholder="Descriptive title…" />
          </div>

          <div className="pf-field" style={{ marginBottom: 0 }}>
            <label className="pf-label">Category</label>
            <select className="pf-input" value={category} onChange={e => setCategory(e.target.value)}>
              {FILE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {error && <p className="pf-error-msg" style={{ marginTop: 10 }}>{error}</p>}
        </div>
        <div className="pf-modal-footer">
          <button className="pf-btn-cancel" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="pf-btn-save" onClick={handleUpload}
            disabled={uploading || !file || !title.trim()}>
            {uploading ? <><span className="pf-spinner" />Uploading…</> : '⬆ Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilesSection({ workspaceId }) {
  const [files, setFiles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    apiFetch(`/api/workspaces/${workspaceId}/files`)
      .then(d => setFiles(Array.isArray(d) ? d : []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  async function openFile(fileId) {
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/files/${fileId}/url`);
      window.open(data.signed_url, '_blank');
    } catch { alert('Could not open file.'); }
  }

  async function doDelete(fileId) {
    setDeleting(fileId);
    try {
      await apiFetch(`/api/workspaces/${workspaceId}/files/${fileId}`, { method: 'DELETE' });
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setDeleting(null); setConfirmDel(null); }
  }

  return (
    <>
      <div className="pf-card">
        <div className="pf-card-header"
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h2 className="pf-card-title">Files & Assets</h2>
            <p className="pf-card-desc">Logos, images, documents and other workspace files.</p>
          </div>
          <button className="pf-btn-save"
            onClick={() => setShowUpload(true)}
            style={{ padding:'8px 16px', fontSize:12, whiteSpace:'nowrap' }}>
            + Add File
          </button>
        </div>

        <div className="pf-section" style={{ borderBottom:'none' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <span className="pf-spinner pf-spinner--dark" />
            </div>
          ) : files.length === 0 ? (
            <div className="pf-empty">
              No files uploaded yet.<br />
              <span style={{ fontSize:11 }}>Add logos, product images, documents and more.</span>
            </div>
          ) : files.map(f => (
            <div key={f.id} className="pf-file-row">
              <div className="pf-file-icon">{fileIcon(f.file_type)}</div>
              <div className="pf-file-info">
                <div className="pf-file-name"
                  onClick={() => openFile(f.id)}
                  style={{ cursor:'pointer', textDecoration:'underline', textDecorationStyle:'dotted' }}>
                  {f.title}
                </div>
                <div className="pf-file-meta">{f.file_name} · {fmtSize(f.file_size)}</div>
              </div>
              <span className="pf-file-badge">
                {FILE_CATEGORIES.find(c => c.value === f.category)?.label || f.category}
              </span>
              <button className="pf-file-delete"
                onClick={() => setConfirmDel(f)}
                disabled={deleting === f.id}
                title="Delete">
                {deleting === f.id ? '…' : '🗑'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {showUpload && (
        <UploadModal
          workspaceId={workspaceId}
          onClose={() => setShowUpload(false)}
          onUploaded={f => setFiles(prev => [f, ...prev])}
        />
      )}

      {confirmDel && (
        <div className="pf-modal-backdrop">
          <div className="pf-modal" style={{ maxWidth: 360 }}>
            <div className="pf-modal-header">
              <h3 className="pf-modal-title">Delete File</h3>
              <button className="pf-modal-close" onClick={() => setConfirmDel(null)}>✕</button>
            </div>
            <div className="pf-modal-body">
              <p style={{ fontSize:13, color:'var(--ink-soft)', margin:0, lineHeight:1.6 }}>
                Delete <strong>{confirmDel.title}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="pf-modal-footer">
              <button className="pf-btn-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="pf-btn-danger" onClick={() => doDelete(confirmDel.id)}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Page root
// ═══════════════════════════════════════════════════════════════════════════════

export default function WorkspaceProfilePage() {
  const [searchParams]      = useSearchParams();
  const { activeWorkspace } = useWorkspaces();
  const drawer              = useDrawer();
  const navigate            = useNavigate();

  const workspaceId = searchParams.get('workspace_id') || activeWorkspace?.id || '';

  const [profile, setProfile]     = useState(() => mergeProfile(null));
  const [original, setOriginal]   = useState(() => mergeProfile(null));
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [savedMsg, setSavedMsg]   = useState(false);
  const [error, setError]         = useState('');
  const [showLeave, setShowLeave] = useState(false);
  const pendingNav                = useRef(null);

  const dirty = JSON.stringify(profile) !== JSON.stringify(original);

  // ── Load profile ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    apiFetch(`/api/workspaces/${workspaceId}/profile`)
      .then(data => {
        const m = mergeProfile(data.profile || {});
        setProfile(m);
        setOriginal(clone(m));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  // ── Warn on browser/tab close ───────────────────────────────────────────────
  useEffect(() => {
    const handler = e => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Field setters ───────────────────────────────────────────────────────────
  function set(section, key, val) {
    setProfile(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
  }
  function setNested(section, sub, key, val) {
    setProfile(prev => ({
      ...prev,
      [section]: { ...prev[section], [sub]: { ...prev[section][sub], [key]: val } },
    }));
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ profile }),
      });
      const m = mergeProfile(data.profile || {});
      setProfile(m);
      setOriginal(clone(m));
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 3000);
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Cancel / navigate ───────────────────────────────────────────────────────
  function safeNavigate(to) {
    if (dirty) { pendingNav.current = to; setShowLeave(true); }
    else navigate(to);
  }

  function handleCancel() { safeNavigate('/dashboard'); }

  function confirmDiscard() {
    setProfile(clone(original));
    setShowLeave(false);
    navigate(pendingNav.current || '/dashboard');
    pendingNav.current = null;
  }

  const p = profile;

  return (
    <AppShell>
      <TopNav
        breadcrumb="Business Profile"
        drawerOpen={drawer.open}
        onToggleDrawer={drawer.toggle}
        drawerPinned={drawer.pinned}
      />
      <NavDrawer
        open={drawer.open}
        onClose={drawer.close}
        pinned={drawer.pinned}
        onTogglePin={drawer.togglePin}
      />

      <BodyGrid drawerPinned={drawer.pinned}>
        <PageBody>

          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontFamily:'var(--serif)', fontSize:26, fontWeight:400, margin:'0 0 4px' }}>
              Business Profile
            </h1>
            <p style={{ fontSize:13, color:'var(--ink-mute)', margin:0 }}>
              Business information used by your AI agents.
            </p>
          </div>

          {dirty && (
            <div className="pf-dirty-banner">
              <span>⚠ You have unsaved changes.</span>
              <div style={{ display:'flex', gap:8 }}>
                <button className="pf-btn-cancel" style={{ padding:'5px 12px', fontSize:12 }}
                  onClick={() => setProfile(clone(original))}>Discard</button>
                <button className="pf-btn-save" style={{ padding:'5px 14px', fontSize:12 }}
                  onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Now'}</button>
              </div>
            </div>
          )}

          {error && <p className="pf-error-msg" style={{ marginBottom:14 }}>{error}</p>}

          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ height:140, borderRadius:12, background:'var(--paper-2)' }} />
              ))}
            </div>
          ) : (
            <>
              {/* Top action bar */}
              <ActionBar dirty={dirty} saving={saving} savedMsg={savedMsg}
                onSave={handleSave} onCancel={handleCancel} />

              {/* 1 — Business */}
              <SectionCard title="🏢 Business" desc="Core company details.">
                <div className="pf-row">
                  <Field label="Business Name" half>
                    <TInput value={p.business.name} onChange={v => set('business','name',v)} placeholder="Acme Inc." />
                  </Field>
                  <Field label="Stage" half>
                    <select className="pf-input" value={p.business.stage}
                      onChange={e => set('business','stage',e.target.value)}>
                      <option value="">Select stage…</option>
                      {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  </div>
                   <div className="pf-row">

                  <Field label="Tagline" half>
                    <TInput value={p.business.tagline} onChange={v => set('business','tagline',v)} placeholder="One-line pitch…" />
                  </Field>
                  <Field label="Founded" half>
                    <TInput value={p.business.founded} onChange={v => set('business','founded',v)} placeholder="2024" />
                  </Field>
                </div>
              </SectionCard>

              {/* 2 — Contact */}
              <SectionCard title="📞 Contact" desc="How to reach you online and offline.">
                <div className="pf-row">
                  <Field label="Website" half>
                    <TInput value={p.contact.website} onChange={v => set('contact','website',v)} placeholder="https://…" />
                  </Field>
                  <Field label="Email" half>
                    <TInput value={p.contact.email} onChange={v => set('contact','email',v)} placeholder="hello@company.com" type="email" />
                  </Field>
                  <Field label="Address">
                    <TInput value={p.contact.address} onChange={v => set('contact','address',v)} placeholder="City, Country" />
                  </Field>
                </div>
                <div style={{ borderTop:'1px solid var(--paper-3)', margin:'14px 0 16px' }} />
                <p className="pf-section-title" style={{ marginBottom:12 }}>Socials</p>
                <div className="pf-row">
                  <Field label="Instagram" half><TInput value={p.contact.socials.instagram} onChange={v => setNested('contact','socials','instagram',v)} placeholder="@handle" /></Field>
                  <Field label="X / Twitter" half><TInput value={p.contact.socials.x} onChange={v => setNested('contact','socials','x',v)} placeholder="@handle" /></Field>
                  <Field label="TikTok" half><TInput value={p.contact.socials.tiktok} onChange={v => setNested('contact','socials','tiktok',v)} placeholder="@handle" /></Field>
                  <Field label="Facebook" half><TInput value={p.contact.socials.facebook} onChange={v => setNested('contact','socials','facebook',v)} placeholder="Page name or URL" /></Field>
                </div>
                <ArrayField label="Phone Numbers" values={p.contact.phone}
                  onChange={v => set('contact','phone',v)} placeholder="+1 555 000 0000" />
              </SectionCard>

              {/* 3 — Product */}
              <SectionCard title="📦 Product" desc="What you sell and how you price it.">
                <div className="pf-field">
                  <label className="pf-label">Core Offering</label>
                  <TArea value={p.product.core_offering} onChange={v => set('product','core_offering',v)}
                    placeholder="Describe your main product or service…" />
                </div>
                <div className="pf-field">
                  <label className="pf-label">Pricing Model</label>
                  <TInput value={p.product.pricing_model} onChange={v => set('product','pricing_model',v)}
                    placeholder="Subscription / One-time / Freemium…" />
                </div>
                <ArrayField label="Key Features" values={p.product.key_features}
                  onChange={v => set('product','key_features',v)} placeholder="Add a feature…" />
              </SectionCard>

              {/* 4 — Market */}
              <SectionCard title="🎯 Market & ICP" desc="Your target customers and competitive landscape.">
                <div className="pf-row">
                  <Field label="ICP Persona" half>
                    <TInput value={p.market.icp.persona} onChange={v => setNested('market','icp','persona',v)} placeholder="e.g. Early-stage SaaS founders" />
                  </Field>
                  <Field label="Industry" half>
                    <TInput value={p.market.icp.industry} onChange={v => setNested('market','icp','industry',v)} placeholder="e.g. B2B SaaS" />
                  </Field>
                  <Field label="TAM Estimate" half>
                    <TInput value={p.market.tam_estimate} onChange={v => set('market','tam_estimate',v)} placeholder="e.g. $4B" />
                  </Field>
                </div>
                <ArrayField label="Pain Points"        values={p.market.icp.pain_points}       onChange={v => setNested('market','icp','pain_points',v)}       placeholder="Add a pain point…" />
                <ArrayField label="Buying Motivations" values={p.market.icp.buying_motivation} onChange={v => setNested('market','icp','buying_motivation',v)} placeholder="Add a motivation…" />
                <ArrayField label="Competitors"        values={p.market.competitors}           onChange={v => set('market','competitors',v)}                   placeholder="Add a competitor…" />
              </SectionCard>

              {/* 5 — Traction */}
              <SectionCard title="📈 Traction" desc="Current growth metrics and key wins.">
                <div className="pf-row">
                  <Field label="Users / Customers" half>
                    <input className="pf-input" type="number" min="0" value={p.traction.users}
                      onChange={e => set('traction','users', Number(e.target.value))} />
                  </Field>
                  <Field label="Revenue (USD)" half>
                    <input className="pf-input" type="number" min="0" value={p.traction.revenue}
                      onChange={e => set('traction','revenue', Number(e.target.value))} />
                  </Field>
                </div>
                <ArrayField label="Key Wins" values={p.traction.key_wins}
                  onChange={v => set('traction','key_wins',v)} placeholder="Add a win…" />
              </SectionCard>

              {/* 6 — Brand */}
              <SectionCard title="🎨 Brand" desc="Voice, tone and visual identity.">
                <div className="pf-field">
                  <label className="pf-label">Brand Tone</label>
                  <TInput value={p.brand.tone} onChange={v => set('brand','tone',v)}
                    placeholder="e.g. Friendly, professional, bold…" />
                </div>
                <ArrayField label="Brand Colors" values={p.brand.colors}
                  onChange={v => set('brand','colors',v)} placeholder="#5dcaa5 or 'Teal'" />
              </SectionCard>

              {/* 7 — Operations */}
              <SectionCard title="⚙️ Operations" desc="Suppliers, processes and infrastructure.">
                <ArrayField label="Suppliers"     values={p.operations.suppliers}     onChange={v => set('operations','suppliers',v)}     placeholder="Add a supplier…" />
                <ArrayField label="Key Processes" values={p.operations.key_processes} onChange={v => set('operations','key_processes',v)} placeholder="Add a process…" />
              </SectionCard>

              {/* 8 — Goals */}
              <SectionCard title="🚀 Goals" desc="Where you're headed.">
                <ArrayField label="Short-term Goals" values={p.goals.short_term} onChange={v => set('goals','short_term',v)} placeholder="Add a short-term goal…" />
                <ArrayField label="Long-term Goals"  values={p.goals.long_term}  onChange={v => set('goals','long_term',v)}  placeholder="Add a long-term goal…" />
              </SectionCard>

              {/* 9 — Constraints */}
              <SectionCard title="⚠️ Constraints" desc="Limitations your AI agents should be aware of.">
                <div className="pf-row">
                  <Field label="Budget" half>
                    <TInput value={p.constraints.budget} onChange={v => set('constraints','budget',v)} placeholder="e.g. $5k/month" />
                  </Field>
                  <Field label="Team Size" half>
                    <TInput value={p.constraints.team_size} onChange={v => set('constraints','team_size',v)} placeholder="e.g. 2 founders" />
                  </Field>
                  <Field label="Time Availability" half>
                    <TInput value={p.constraints.time} onChange={v => set('constraints','time',v)} placeholder="e.g. 20 hrs/week" />
                  </Field>
                </div>
                <div className="pf-field">
                  <label className="pf-label">Other Notes</label>
                  <TArea value={p.constraints.notes} onChange={v => set('constraints','notes',v)}
                    placeholder="Any other constraints agents should know…" />
                </div>
              </SectionCard>

              {/* 10 — Assets */}
              <SectionCard title="🗂 Assets" desc="Existing digital assets and campaigns.">
                <ArrayField label="Landing Pages" values={p.assets.landing_pages} onChange={v => set('assets','landing_pages',v)} placeholder="https://…" />
                <ArrayField label="Content"       values={p.assets.content}       onChange={v => set('assets','content',v)}       placeholder="Blog post, video name…" />
                <ArrayField label="Campaigns"     values={p.assets.campaigns}     onChange={v => set('assets','campaigns',v)}     placeholder="Campaign name…" />
              </SectionCard>

              {/* Bottom action bar */}
              <ActionBar dirty={dirty} saving={saving} savedMsg={savedMsg}
                onSave={handleSave} onCancel={handleCancel} />

              {/* Files — saves immediately, separate from profile */}
              <div style={{ marginTop: 32 }}>
                <div style={{ marginBottom: 12 }}>
                  <h2 style={{ fontFamily:'var(--serif)', fontSize:20, fontWeight:400, margin:'0 0 3px' }}>Files</h2>
                  <p style={{ fontSize:12, color:'var(--ink-mute)', margin:0 }}>
                    Uploaded immediately — not part of the save/cancel flow above.
                  </p>
                </div>
                <FilesSection workspaceId={workspaceId} />
              </div>
            </>
          )}
        </PageBody>
      </BodyGrid>

      {showLeave && (
        <LeaveModal
          onStay={() => { setShowLeave(false); pendingNav.current = null; }}
          onDiscard={confirmDiscard}
        />
      )}
    </AppShell>
  );
}